import { logFrontendError, tryGetErrorLogContext } from "@/hooks/use-error-logger";

/**
 * Registers global error handlers that capture errors which would otherwise
 * go unlogged: uncaught exceptions, unhandled promise rejections.
 *
 * Call once at app startup inside a useEffect (in a component mounted under
 * the AuthProvider so Supabase is initialised before any handlers fire).
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
let initialised = false;

export function initGlobalErrorHandlers(): void {
  if (initialised) return;
  initialised = true;

  // ── Uncaught synchronous errors ──────────────────────────────────────────
  window.onerror = (message, source, lineno, colno, error) => {
    const msg =
      error
        ? `${error.name}: ${error.message}`
        : typeof message === "string"
          ? message
          : "Unknown browser error";

    tryGetErrorLogContext().then((ctx) => {
      if (!ctx) return;
      logFrontendError({
        organizationId: ctx.orgId,
        userId: ctx.userId,
        error: msg,
        errorType: error?.name ?? "Error",
        errorSource: "browser",
        stackTrace: error?.stack,
        context: source ? `${source}:${lineno}:${colno}` : undefined,
      });
    });

    return false; // don't suppress the browser's default console output
  };

  // ── Unhandled promise rejections ─────────────────────────────────────────
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg =
      reason instanceof Error
        ? `${reason.name}: ${reason.message}`
        : String(reason ?? "Unhandled promise rejection");

    tryGetErrorLogContext().then((ctx) => {
      if (!ctx) return;
      logFrontendError({
        organizationId: ctx.orgId,
        userId: ctx.userId,
        error: msg,
        errorType: reason instanceof Error ? reason.name : "UnhandledRejection",
        errorSource: "promise",
        stackTrace: reason instanceof Error ? reason.stack : undefined,
      });
    });
  });
}
